import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { reportMessage } from '@/lib/sentry';

// Resubscribe schedule: CHANNEL_ERROR is usually
// permanent (RLS, auth, channel config), so a capped exponential backoff
// self-heals a blip without spamming the realtime endpoint forever.
const MAX_REALTIME_RETRY_ATTEMPTS = 5;
const REALTIME_RETRY_BASE_MS = 2000;
const REALTIME_RETRY_MAX_MS = 30_000;

/**
 * A processing run writes the same `emails` row several times in a second
 * (RECEIVED → PROCESSING → REVIEW_REQUIRED, plus metadata updates). One
 * trailing-edge window turns that burst into a single re-read.
 */
export const EMAILS_CHANGED_DEBOUNCE_MS = 400;

/**
 * Fires `onChange` whenever a row in `emails` the user can see is inserted,
 * updated or deleted (YAR-442).
 *
 * This is deliberately an UNFILTERED subscription. The Review feed scopes
 * its query by `inbox_address_id IN (…)`; a `postgres_changes` filter takes
 * one column with eq/neq/lt/gt and cannot express IN, and `household_id` is
 * nullable and a different predicate — so a filtered subscription would
 * drop rows silently, which is why this surface had no realtime for a long
 * time. Listening to everything RLS lets through and re-running the feed's
 * own query on each event has no such gap: the query stays the single
 * source of truth, and the subscription is only a hint that it is stale.
 * The cost is a spare silent refetch when a sibling household's mail moves,
 * which is rare and invisible.
 *
 * `onChange` is read through a ref so callers can pass a fresh closure each
 * render without re-subscribing.
 */
export function useEmailsChanged(
  onChange: () => void,
  {
    enabled = true,
    channel: channelName = 'emails-changed',
  }: { enabled?: boolean; channel?: string } = {},
): void {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [retryNonce, setRetryNonce] = useState(0);
  const retryAttemptsRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        onChangeRef.current();
      }, EMAILS_CHANGED_DEBOUNCE_MS);
    };

    // A channel name per consumer: supabase-js treats a channel name as a
    // shared topic, so two consumers on one name (the Review feed and the
    // email status toast) would entangle their subscriptions.
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emails' },
        scheduleChange,
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          retryAttemptsRef.current = 0;
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (retryAttemptsRef.current >= MAX_REALTIME_RETRY_ATTEMPTS) {
            // Out of retries: this consumer gets no more hints this session.
            // Report it once so a silent channel leaves a trace (YAR-477).
            if (retryAttemptsRef.current === MAX_REALTIME_RETRY_ATTEMPTS) {
              retryAttemptsRef.current += 1;
              reportMessage('realtime.emails_channel_gave_up', {
                channel: channelName,
                status,
              });
            }
            return;
          }
          const attempt = retryAttemptsRef.current;
          retryAttemptsRef.current = attempt + 1;
          const delay = Math.min(
            REALTIME_RETRY_BASE_MS * 2 ** attempt,
            REALTIME_RETRY_MAX_MS,
          );
          retryTimerRef.current = setTimeout(() => {
            setRetryNonce((n) => n + 1);
          }, delay);
        }
      });

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [enabled, channelName, retryNonce]);
}
